// src/components/StaffManagement.jsx
import { useState } from 'react'

function StaffManagement({ staff, onUpdateRole, onPermissions, session }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [showFullProfile, setShowFullProfile] = useState({})

  const toggleFullProfile = (id) => {
    setShowFullProfile(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  // Filter staff based on search and filters
  const filteredStaff = staff.filter(member => {
    const searchLower = searchTerm.toLowerCase()
    const matchesSearch = 
      member.email?.toLowerCase().includes(searchLower) ||
      member.first_name?.toLowerCase().includes(searchLower) ||
      member.last_name?.toLowerCase().includes(searchLower) ||
      member.full_name?.toLowerCase().includes(searchLower) ||
      member.phone?.includes(searchTerm) ||
      member.qualification?.toLowerCase().includes(searchLower) ||
      member.department?.toLowerCase().includes(searchLower) ||
      member.position?.toLowerCase().includes(searchLower)

    const matchesRole = filterRole === 'all' || member.role === filterRole
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && member.is_active !== false) ||
      (filterStatus === 'inactive' && member.is_active === false)

    return matchesSearch && matchesRole && matchesStatus
  })

  const roles = ['leadership', 'agent', 'accounting', 'marketing', 'legal', 'recruitment', 'cro']

  const roleColors = {
    'leadership': '#1e3a5f',
    'agent': '#059669',
    'accounting': '#f59e0b',
    'marketing': '#2c7da0',
    'legal': '#8b5cf6',
    'recruitment': '#ec489a',
    'cro': '#14b8a6'
  }

  const roleIcons = {
    'leadership': '👑',
    'agent': '🤝',
    'accounting': '💰',
    'marketing': '📢',
    'legal': '⚖️',
    'recruitment': '🎯',
    'cro': '👥'
  }

  const getRoleColor = (role) => roleColors[role] || '#64748b'
  const getRoleIcon = (role) => roleIcons[role] || '👤'

  // Group staff by role for stats
  const staffByRole = roles.reduce((acc, role) => {
    acc[role] = staff.filter(s => s.role === role).length
    return acc
  }, {})

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h2 style={titleStyle}>👥 Staff Management</h2>
        <p style={subtitleStyle}>View and manage all staff records from CRO Portal</p>
        <p style={staffCountStyle}>{staff.length} Total Staff Members</p>
      </div>

      {/* Stats Cards */}
      <div style={statsContainerStyle}>
        <div style={statCardStyle}>
          <span style={statNumberStyle}>{staff.length}</span>
          <span style={statLabelStyle}>Total Staff</span>
        </div>
        {roles.slice(0, 4).map(role => (
          <div key={role} style={statCardStyle}>
            <span style={statNumberStyle}>{staffByRole[role] || 0}</span>
            <span style={statLabelStyle}>{getRoleIcon(role)} {role.charAt(0).toUpperCase() + role.slice(1)}</span>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div style={filterContainerStyle}>
        <input
          type="text"
          placeholder="🔍 Search by name, email, phone, qualification..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={searchInputStyle}
        />
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="all">All Roles</option>
          {roles.map(role => (
            <option key={role} value={role}>
              {getRoleIcon(role)} {role.charAt(0).toUpperCase() + role.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="all">All Status</option>
          <option value="active">✅ Active</option>
          <option value="inactive">❌ Inactive</option>
        </select>
      </div>

      {/* Staff Cards */}
      {filteredStaff.length === 0 ? (
        <div style={emptyStateStyle}>
          <p>No staff members found. CRO Portal has not added any staff records yet.</p>
        </div>
      ) : (
        <div style={gridStyle}>
          {filteredStaff.map(member => {
            const isExpanded = showFullProfile[member.id] || false
            
            return (
              <div key={member.id} style={cardStyle}>
                {/* Card Header */}
                <div style={cardHeaderStyle}>
                  <div style={{...avatarStyle, background: getRoleColor(member.role)}}>
                    {member.first_name?.charAt(0) || member.email?.charAt(0) || '👤'}
                  </div>
                  <div style={cardTitleStyle}>
                    <h4 style={nameStyle}>
                      {member.first_name || ''} {member.last_name || ''}
                      {member.position && <span style={positionBadgeStyle}>{member.position}</span>}
                    </h4>
                    <p style={emailStyle}>{member.email}</p>
                  </div>
                  <span style={{...roleBadgeStyle, background: getRoleColor(member.role)}}>
                    {getRoleIcon(member.role)} {member.role || 'No Role'}
                  </span>
                </div>

                {/* Basic Details */}
                <div style={detailsGridStyle}>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>📧 Email</span>
                    <span style={detailValueStyle}>{member.email || 'N/A'}</span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>📞 Phone</span>
                    <span style={detailValueStyle}>{member.phone || 'N/A'}</span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>🏢 Department</span>
                    <span style={detailValueStyle}>{member.department || 'N/A'}</span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>🎓 Qualification</span>
                    <span style={detailValueStyle}>{member.qualification || 'N/A'}</span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>📍 Address</span>
                    <span style={detailValueStyle}>{member.address || 'N/A'}</span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>📅 Hire Date</span>
                    <span style={detailValueStyle}>
                      {member.hire_date ? new Date(member.hire_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>🎂 Date of Birth</span>
                    <span style={detailValueStyle}>
                      {member.date_of_birth ? new Date(member.date_of_birth).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>🆔 Status</span>
                    <span style={{
                      ...statusBadgeStyle,
                      background: member.is_active !== false ? '#d1fae5' : '#fee2e2',
                      color: member.is_active !== false ? '#059669' : '#dc2626'
                    }}>
                      {member.is_active !== false ? '✅ Active' : '❌ Inactive'}
                    </span>
                  </div>
                </div>

                {/* Bio */}
                {member.bio && (
                  <div style={bioStyle}>
                    <span style={detailLabelStyle}>📝 Bio</span>
                    <p style={bioTextStyle}>{member.bio}</p>
                  </div>
                )}

                {/* Emergency Contact */}
                {member.emergency_contact && (
                  <div style={bioStyle}>
                    <span style={detailLabelStyle}>🚨 Emergency Contact</span>
                    <p style={bioTextStyle}>{member.emergency_contact}</p>
                  </div>
                )}

                {/* Meta Info */}
                <div style={metaStyle}>
                  <span style={metaTextStyle}>
                    Added: {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                  {member.onboarding_completed && (
                    <span style={{...metaTextStyle, color: '#059669'}}>✅ Onboarding Complete</span>
                  )}
                </div>

                {/* Actions */}
                {session?.user?.role === 'leadership' && (
                  <div style={actionStyle}>
                    <select
                      value={member.role || ''}
                      onChange={(e) => onUpdateRole(member.id, e.target.value)}
                      style={roleSelectStyle}
                    >
                      {roles.map(role => (
                        <option key={role} value={role}>
                          {getRoleIcon(role)} {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => onPermissions(member)}
                      style={permissionBtnStyle}
                    >
                      🔑 Permissions
                    </button>
                    <button
                      onClick={() => toggleFullProfile(member.id)}
                      style={expandBtnStyle}
                    >
                      {isExpanded ? '📕 Hide Full Profile' : '📖 View Full Profile'}
                    </button>
                  </div>
                )}

                {/* Expanded Full Profile */}
                {isExpanded && (
                  <div style={expandedStyle}>
                    <h5 style={expandedTitleStyle}>📋 Full Profile</h5>
                    <div style={expandedGridStyle}>
                      <div><strong>First Name:</strong> {member.first_name || 'N/A'}</div>
                      <div><strong>Last Name:</strong> {member.last_name || 'N/A'}</div>
                      <div><strong>Full Name:</strong> {member.full_name || 'N/A'}</div>
                      <div><strong>Email:</strong> {member.email || 'N/A'}</div>
                      <div><strong>Phone:</strong> {member.phone || 'N/A'}</div>
                      <div><strong>Role:</strong> {member.role || 'N/A'}</div>
                      <div><strong>Department:</strong> {member.department || 'N/A'}</div>
                      <div><strong>Position:</strong> {member.position || 'N/A'}</div>
                      <div><strong>Qualification:</strong> {member.qualification || 'N/A'}</div>
                      <div><strong>Address:</strong> {member.address || 'N/A'}</div>
                      <div><strong>Date of Birth:</strong> {member.date_of_birth || 'N/A'}</div>
                      <div><strong>Hire Date:</strong> {member.hire_date || 'N/A'}</div>
                      <div><strong>Status:</strong> {member.is_active !== false ? 'Active' : 'Inactive'}</div>
                      <div><strong>Onboarding:</strong> {member.onboarding_completed ? '✅ Completed' : '⏳ Pending'}</div>
                    </div>
                    {member.bio && (
                      <div style={{marginTop: '8px'}}><strong>Bio:</strong> {member.bio}</div>
                    )}
                    {member.emergency_contact && (
                      <div style={{marginTop: '4px'}}><strong>Emergency Contact:</strong> {member.emergency_contact}</div>
                    )}
                    <div style={{marginTop: '8px', fontSize: '11px', color: '#94a3b8'}}>
                      Created: {member.created_at ? new Date(member.created_at).toLocaleString() : 'N/A'}
                      {member.updated_at && ` | Updated: ${new Date(member.updated_at).toLocaleString()}`}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Styles
const containerStyle = {
  padding: '20px'
}

const headerStyle = {
  marginBottom: '24px'
}

const titleStyle = {
  fontSize: '24px',
  fontWeight: '700',
  color: '#1e3a5f',
  margin: 0
}

const subtitleStyle = {
  color: '#64748b',
  fontSize: '14px',
  marginTop: '4px'
}

const staffCountStyle = {
  color: '#64748b',
  fontSize: '13px',
  marginTop: '4px',
  fontWeight: '500'
}

const statsContainerStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
  gap: '10px',
  marginBottom: '24px'
}

const statCardStyle = {
  background: '#fff',
  padding: '14px',
  borderRadius: '12px',
  textAlign: 'center',
  border: '1px solid #e2e8f0',
  display: 'flex',
  flexDirection: 'column'
}

const statNumberStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#1e3a5f'
}

const statLabelStyle = {
  fontSize: '11px',
  color: '#64748b'
}

const filterContainerStyle = {
  display: 'flex',
  gap: '12px',
  marginBottom: '24px',
  flexWrap: 'wrap'
}

const searchInputStyle = {
  flex: 1,
  padding: '10px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '14px',
  minWidth: '200px'
}

const filterSelectStyle = {
  padding: '10px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '14px',
  background: '#fff'
}

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
  gap: '20px'
}

const cardStyle = {
  background: '#fff',
  borderRadius: '16px',
  padding: '20px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
}

const cardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '14px',
  paddingBottom: '12px',
  borderBottom: '1px solid #e2e8f0',
  flexWrap: 'wrap'
}

const avatarStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: '#1e3a5f',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px',
  fontWeight: '600',
  flexShrink: 0
}

const cardTitleStyle = {
  flex: 1
}

const nameStyle = {
  margin: 0,
  fontSize: '15px',
  fontWeight: '600',
  color: '#1e293b'
}

const positionBadgeStyle = {
  fontSize: '10px',
  background: '#f1f5f9',
  color: '#64748b',
  padding: '2px 8px',
  borderRadius: '10px',
  marginLeft: '6px',
  fontWeight: '400'
}

const emailStyle = {
  margin: 0,
  fontSize: '12px',
  color: '#64748b'
}

const roleBadgeStyle = {
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '11px',
  color: '#fff',
  display: 'inline-block'
}

const statusBadgeStyle = {
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  display: 'inline-block'
}

const detailsGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '6px 12px',
  marginBottom: '10px'
}

const detailItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1px'
}

const detailLabelStyle = {
  fontSize: '10px',
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}

const detailValueStyle = {
  fontSize: '13px',
  color: '#1e293b',
  fontWeight: '500'
}

const bioStyle = {
  marginTop: '8px',
  paddingTop: '10px',
  borderTop: '1px solid #f1f5f9'
}

const bioTextStyle = {
  margin: '2px 0 0 0',
  fontSize: '13px',
  color: '#475569',
  lineHeight: '1.4'
}

const metaStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '8px',
  fontSize: '11px',
  color: '#94a3b8'
}

const metaTextStyle = {
  fontSize: '11px',
  color: '#94a3b8'
}

const actionStyle = {
  display: 'flex',
  gap: '8px',
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '1px solid #e2e8f0',
  flexWrap: 'wrap'
}

const roleSelectStyle = {
  padding: '6px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '13px',
  flex: 1,
  minWidth: '100px'
}

const permissionBtnStyle = {
  padding: '6px 16px',
  background: '#2c7da0',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px'
}

const expandBtnStyle = {
  padding: '6px 16px',
  background: 'transparent',
  color: '#1e3a5f',
  border: '1px solid #1e3a5f',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px'
}

const emptyStateStyle = {
  textAlign: 'center',
  padding: '60px 20px',
  color: '#64748b'
}

const expandedStyle = {
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '2px solid #1e3a5f',
  background: '#f8fafc',
  padding: '16px',
  borderRadius: '8px'
}

const expandedTitleStyle = {
  margin: '0 0 12px 0',
  fontSize: '14px',
  color: '#1e3a5f'
}

const expandedGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '6px 16px',
  fontSize: '13px',
  lineHeight: '1.8'
}

export default StaffManagement